// Admin API client — /admin/platform-health/* (mirrors the API's platform-health aggregate).

import type {
  PlatformHealthCriticalResponse,
  PlatformHealthAxonFindingsResponse,
  PlatformHealthAxonOverviewResponse,
  PlatformHealthOverviewResponse,
  PlatformHealthStateInvariantsResponse,
  PlatformHealthAuditAssertionsResponse,
  PlatformHealthEmailDeliveryResponse,
  PlatformHealthAxonRunDetailResponse,
  PlatformHealthAxonRunsResponse,
  PlatformHealthPrismResponse,
  PlatformHealthPrismPreSendResponse,
  PlatformHealthPulseResponse,
  PlatformHealthMantisResponse,
  PlatformHealthAquixRunsResponse,
  PlatformHealthAquixRunDetailResponse,
  PlatformHealthRavenStagingReleasesResponse,
  PlatformHealthRavenStagingReleaseDetailResponse,
  AxonRunsModel,
} from '@/pages/platform-health/types';
import { client, rawAdminFetch } from './transport';

export const platformHealthApi = {
  // Platform Health — Critical (A7Y-2325). Backed by
  // a7y-api/src/routes/admin/platform-health.ts.
  getPlatformHealthCritical: () =>
    client.get<{ data: PlatformHealthCriticalResponse }>('/platform-health/critical'),
  getPlatformHealthOverview: () =>
    client.get<{ data: PlatformHealthOverviewResponse }>('/platform-health/overview'),
  getPlatformHealthAxonOverview: (environment?: string) =>
    client.get<{ data: PlatformHealthAxonOverviewResponse }>(
      `/platform-health/axon/overview${environment ? `?environment=${encodeURIComponent(environment)}` : ''}`
    ),
  getPlatformHealthAxonFindings: (environment?: string) =>
    client.get<{ data: PlatformHealthAxonFindingsResponse }>(
      `/platform-health/axon/findings${environment ? `?environment=${encodeURIComponent(environment)}` : ''}`
    ),
  getPlatformHealthRuns: (
    model: AxonRunsModel,
    params?: {
      environment?: string;
      subsystem?: string;
      status?: string;
      time?: string;
      release?: string;
      finding?: string;
      search?: string;
      limit?: number;
    }
  ) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    return client.get<{ data: PlatformHealthAxonRunsResponse }>(
      `/platform-health/${model}/runs${query.size ? `?${query.toString()}` : ''}`
    );
  },
  getPlatformHealthRunDetail: (model: AxonRunsModel, runKey: string, environment?: string) =>
    client.get<{ data: PlatformHealthAxonRunDetailResponse }>(
      `/platform-health/${model}/runs/${encodeURIComponent(runKey)}${
        environment ? `?environment=${encodeURIComponent(environment)}` : ''
      }`
    ),
  getPlatformHealthPulse: (params?: {
    environment?: string;
    runtime?: string;
    owner?: string;
    family?: string;
    status?: string;
    migrationState?: string;
    search?: string;
  }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value) query.set(key, value);
    }
    return client.get<{ data: PlatformHealthPulseResponse }>(
      `/platform-health/pulse${query.size ? `?${query.toString()}` : ''}`
    );
  },
  getPlatformHealthMantis: (params?: {
    environment?: string;
    subsystem?: string;
    impact?: string;
    actionKind?: string;
    lifecycle?: string;
    sla?: string;
    owner?: string;
    search?: string;
    prominent?: string;
  }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value) query.set(key, value);
    }
    return client.get<{ data: PlatformHealthMantisResponse }>(
      `/platform-health/mantis${query.size ? `?${query.toString()}` : ''}`
    );
  },
  getPlatformHealthPrism: (params?: { environment?: string }) => {
    const query = new URLSearchParams();
    if (params?.environment) query.set('environment', params.environment);
    return client.get<{ data: PlatformHealthPrismResponse }>(
      `/platform-health/prism${query.size ? `?${query.toString()}` : ''}`
    );
  },
  // A7Y-5454 — the pre-send review trial, and releasing a held audit email.
  getPlatformHealthPrismPreSend: (params?: { adjudicationLimit?: number }) =>
    client.get<{ data: PlatformHealthPrismPreSendResponse }>(
      `/platform-health/prism/pre-send${
        params?.adjudicationLimit ? `?adjudicationLimit=${params.adjudicationLimit}` : ''
      }`
    ),
  releasePrismPreSendHold: (auditId: string) =>
    client.post<{ data: { auditId: string; releasedAt: string; task: string } }>(
      `/platform-health/prism/pre-send/${encodeURIComponent(auditId)}/release`
    ),
  getPlatformHealthAquixRuns: (environment: 'staging' | 'production', limit = 30) =>
    client.get<{ data: PlatformHealthAquixRunsResponse }>(
      `/platform-health/aquix/runs?environment=${encodeURIComponent(environment)}&limit=${limit}`
    ),
  getPlatformHealthAquixRun: (environment: 'staging' | 'production', runId: string) =>
    client.get<{ data: PlatformHealthAquixRunDetailResponse }>(
      `/platform-health/aquix/runs/${encodeURIComponent(runId)}?environment=${encodeURIComponent(
        environment
      )}`
    ),
  getPlatformHealthAquixEvidence: async (
    environment: 'staging' | 'production',
    runId: string,
    key: string
  ) => {
    const query = new URLSearchParams({ environment, runId, key });
    const response = await rawAdminFetch(`/platform-health/aquix/evidence?${query}`);
    if (!response.ok) {
      throw new Error(`AQUIX evidence request failed: ${response.status} ${response.statusText}`);
    }
    return response.blob();
  },
  getPlatformHealthRavenStagingReleases: (params?: {
    limit?: number;
    verdict?: string;
    flag?: string;
    search?: string;
    readMode?: string;
  }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    return client.get<{ data: PlatformHealthRavenStagingReleasesResponse }>(
      `/platform-health/raven/staging-releases${query.size ? `?${query.toString()}` : ''}`
    );
  },
  getPlatformHealthRavenStagingRelease: (releaseId: string, readMode?: string) =>
    client.get<{ data: PlatformHealthRavenStagingReleaseDetailResponse }>(
      `/platform-health/raven/staging-releases/${encodeURIComponent(releaseId)}${
        readMode ? `?readMode=${encodeURIComponent(readMode)}` : ''
      }`
    ),
  getPlatformHealthStateInvariants: () =>
    client.get<{ data: PlatformHealthStateInvariantsResponse }>(
      '/platform-health/state-invariants'
    ),
  getPlatformHealthAuditAssertions: () =>
    client.get<{ data: PlatformHealthAuditAssertionsResponse }>(
      '/platform-health/audit-assertions'
    ),
  getPlatformHealthEmailDelivery: () =>
    client.get<{ data: PlatformHealthEmailDeliveryResponse }>('/platform-health/email-delivery'),
};
